import { useBoardEditStore } from '@/board/stores/boardEditStore';
import { useSurveyEditStore } from '@/surveys/stores/surveyEditStore';
import Cookies from 'js-cookie';

export type HuddleResponse<T> = {
	success: true,
	data: T
} | {
	success: false,
	formErrors?: DjangoErrorDict,
	message?: string,
	code?: string
};

export type FetchResult = { json: HuddleResponse<any>, response: Response };
export type FetchResultTyped<T> = { json: HuddleResponse<T>, response: Response };

export type KnownFetchResult<T> = { json: HuddleResponse<T>, response: Response };

export type DjangoErrorDict = {[field: string]: Array<{message: string, code?: string}>};
export type ErrorResult = { json: DjangoErrorDict, response: Response };

export type FetcherOptions = RequestInit & {
	noContentType?: boolean
};

function doFetch(
	url: string,
	method: string,
	body: any,
	optionOverrides: FetcherOptions | null = null
): Promise<FetchResult> {
	let options: any = {
		method,
		headers: {},
		mode: 'same-origin'
	};

	if (optionOverrides === null || (optionOverrides.noContentType === undefined || !optionOverrides.noContentType)) {
		options.headers["Content-Type"] = "application/json;charset=UTF-8";
	}

	if (optionOverrides !== null) {
		if (optionOverrides.noContentType !== undefined) {
			delete optionOverrides.noContentType;
		}

		if (optionOverrides.mode !== undefined) {
			options.mode = optionOverrides.mode;
		}

		if (optionOverrides.headers !== undefined) {
			options.headers = { ...options.headers, ...optionOverrides.headers };
		}
	}

	// A little unclean should eventually pull this up and out and update all the call sites
	try {
		const { consumerChannel } = useBoardEditStore();
		if (consumerChannel) {
			options.headers['X-CONSUMER-ID'] = consumerChannel;
		}
	} catch(e) {}

	// A little unclean should eventually pull this up and out and update all the call sites
	try {
		const { consumerChannel } = useSurveyEditStore();
		if (consumerChannel) {
			options.headers['X-CONSUMER-ID'] = consumerChannel;
		}
	} catch(e) {}

	let cookieName = 'csrftoken';

	let csrftoken = Cookies.get(cookieName) as string | undefined;

	if (csrftoken === undefined) {
		const csrfinput = document.querySelector('[name=csrfmiddlewaretoken]') as HTMLInputElement | null;
		if (csrfinput) {
			csrftoken = csrfinput.value;
		}
	}

	if (csrftoken) {
		options.headers["X-CSRFToken"] = csrftoken;
	}

	if (body !== null) {
		if (options.headers !== undefined
			&& options.headers["Content-Type"] !== undefined
			&& options.headers["Content-Type"].indexOf('application/json') > -1
		) {
			options.body = JSON.stringify(body);
		} else {
			options.body = body;
		}
	}

	return fetch(
		url,
		options
	)
	.then(response => {
		return response.json()
			.then(data => ({
				json: data,
				response: response
			}) as FetchResult)
			.catch(data => ({
				json: data,
				response: response
			}) as FetchResult);
	});
}

export const fetcher = {
	Post(url: string, body: any = null, options: any = null): Promise<FetchResult> {
		return doFetch(url, 'POST', body, options);
	},
	Get(url: string, options: any = null): Promise<FetchResult> {
		return doFetch(url, 'GET', null, options);
	},
	Put(url: string, body: any = null, options: any = null): Promise<FetchResult> {
		return doFetch(url, 'PUT', body, options);
	},
	Patch(url: string, body: any = null, options: any = null): Promise<FetchResult> {
		return doFetch(url, 'PATCH', body, options);
	},
	Delete(url: string, body: any = null, options: any = null): Promise<FetchResult> {
		return doFetch(url, 'DELETE', body, options);
	}
};
